import { useEffect, useState } from 'react';
import { Button } from './Button';
import { PlusIcon, XIcon } from './icons';

// The brief: "Ship it as an installable PWA ... This is how everyone gets it on
// their phone; there's no app store build." A manifest alone makes it
// *possible* to install; nobody discovers it. This is the nudge.

/** Chrome fires this so a page can defer and re-trigger the install banner. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'tabi.install-dismissed';

function isStandalone(): boolean {
  // Guarded because this runs during the first render of the whole app. Not
  // every environment has matchMedia — older Android webviews are the real
  // case, jsdom is the one that caught it — and an exception thrown here
  // unmounts everything above it. A missing display-mode check is worth a
  // redundant install hint; it is not worth a blank screen.
  try {
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches
    ) {
      return true;
    }
  } catch {
    /* fall through to the iOS check */
  }
  // iOS Safari predates the standard and only exposes this.
  return (window.navigator as { standalone?: boolean }).standalone === true;
}

function isIos(): boolean {
  const ua = window.navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  // iPadOS 13+ reports itself as "Macintosh". The touch-point count is the
  // only reliable way to tell an iPad from a Mac, and getting it wrong means
  // an iPad is shown desktop copy and never gets installed.
  return /macintosh/i.test(ua) && window.navigator.maxTouchPoints > 1;
}

/**
 * On Android/Chrome this captures beforeinstallprompt and offers a real
 * install button.
 *
 * iOS has no such API — Add to Home Screen is a manual gesture buried in the
 * Share sheet, and it only exists in Safari, not Chrome or Firefox on iOS.
 * There is nothing to trigger, so iOS gets instructions instead. Getting this
 * wrong is how a group ends up with three people who "couldn't find the app".
 */
export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    const onBeforeInstall = (event: Event) => {
      event.preventDefault(); // stop Chrome's own mini-infobar
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setInstalled(true);

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  function dismiss() {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      /* private mode: it just reappears next visit */
    }
  }

  // Only two reasons to stay quiet: it is already installed, or the person
  // said no. A desktop browser still gets a line, because the sign-in page is
  // where someone reads the link before opening it on their phone — and the
  // brief is explicit that installing is how everybody actually gets this app.
  if (installed || dismissed) return null;

  const platform: 'prompt' | 'ios' | 'other' = deferred ? 'prompt' : isIos() ? 'ios' : 'other';

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3) var(--sp-4)',
        background: 'var(--accent-subtle)',
        border: '1px solid var(--accent-soft)',
        borderRadius: 'var(--r-md)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0, fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)', lineHeight: 1.45 }}>
        {platform === 'prompt' && (
          <>
            <strong style={{ color: 'var(--text)' }}>Add Tabi to your home screen</strong> so it
            opens like an app.
          </>
        )}
        {platform === 'ios' && (
          <>
            <strong style={{ color: 'var(--text)' }}>Add Tabi to your home screen:</strong> tap
            Share, then <em>Add to Home Screen</em>. Safari only — it isn’t there in Chrome on
            iPhone.
          </>
        )}
        {platform === 'other' && (
          <>
            <strong style={{ color: 'var(--text)' }}>Open this on your phone</strong> to add Tabi
            to your home screen. That is how everyone on the trip should use it.
          </>
        )}
      </div>

      {/* Narrowed on `deferred` itself, not on `platform` — the derived string
          tells TypeScript nothing about whether the event is still there. */}
      {deferred && (
        <Button
          size="sm"
          onClick={() => {
            void deferred.prompt();
            void deferred.userChoice.finally(() => setDeferred(null));
          }}
        >
          <PlusIcon size={15} />
          Install
        </Button>
      )}

      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install suggestion"
        style={{
          background: 'none',
          border: 'none',
          padding: 4,
          cursor: 'pointer',
          color: 'var(--text-muted)',
          flex: '0 0 auto',
        }}
      >
        <XIcon size={15} />
      </button>
    </div>
  );
}
